import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { userService } from './user.service';

export const authService = {
  // Sign up with email and password
  async register(email, password, name) {
    try {
      // Create auth user
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with name
      await updateProfile(user, { displayName: name });
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        hasCompletedOnboarding: false
      });
      
      // Send verification email
      await sendEmailVerification(user);
      
      return this.handleAuthSuccess(user, true);
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Sign in with email and password
  async login(email, password) {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      return this.handleAuthSuccess(user, false);
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Sign in with Google
  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      const isNewUser = result.additionalUserInfo?.isNewUser;
      
      // If new user, create Firestore document
      if (isNewUser) {
        await setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          hasCompletedOnboarding: false
        });
      }
      
      return this.handleAuthSuccess(result.user, isNewUser);
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Sign out
  async logout() {
    try {
      await signOut(auth);
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Reset password
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Error handler
  handleError(error) {
    console.error('Auth error:', error);
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        return new Error('This email is already registered. Please try logging in.');
      case 'auth/invalid-email':
        return new Error('Invalid email address.');
      case 'auth/operation-not-allowed':
        return new Error('Email/password accounts are not enabled. Please contact support.');
      case 'auth/weak-password':
        return new Error('Password should be at least 6 characters.');
      case 'auth/user-disabled':
        return new Error('This account has been disabled. Please contact support.');
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return new Error('Invalid email or password.');
      default:
        return new Error('An error occurred. Please try again.');
    }
  },

  async handleAuthSuccess(user, isNewUser) {
    // For new users or unverified emails, redirect to verification
    if (isNewUser || !user.emailVerified) {
      return {
        user,
        redirectTo: '/verify-email'
      };
    }

    // Only check onboarding status for verified emails
    const hasCompletedOnboarding = await userService.hasCompletedOnboarding(user.uid);
    
    return {
      user,
      redirectTo: hasCompletedOnboarding ? '/dashboard' : '/onboarding'
    };
  },
}; 